import { sql } from 'drizzle-orm';
import {
  pgTable,
  index,
  jsonb,
  timestamp,
  varchar,
  text,
  integer,
  decimal,
  boolean,
  uuid,
  primaryKey
} from "drizzle-orm/pg-core";
import { claims } from './claims';
import { documentGenerationStatusEnum, documentTemplateStatusEnum, documentTemplateTypeEnum, documentTypeEnum } from './enums';
import { leads } from './leads';
import { organizations } from './organizations';
import { reminderConfigurations } from './tasks';
import { users } from './users';

// Folders table for document organization
export const folders = pgTable("folders", {
  id: varchar("id").primaryKey(),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  name: varchar("name").notNull(),
  description: text("description"),
  isDefault: boolean("is_default").default(false),
  createdBy: varchar("created_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_folders_claim_id").on(table.claimId),
  index("idx_folders_is_default").on(table.isDefault),
]);


// Documents table with enhanced OCR and classification capabilities
export const documents: any = pgTable("documents", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }),
  leadId: uuid("lead_id").references(() => leads.id, { onDelete: "cascade" }),
  fileName: varchar("file_name").notNull(),
  displayName: varchar("display_name"), // AI-generated display name in format "YYYY-MM-DD - Description.ext"
  fileSize: integer("file_size"),
  fileType: varchar("file_type"),
  documentType: documentTypeEnum("document_type").notNull(),
  
  // Storage location - Google Drive or Replit Object Storage
  filePath: text("file_path"), // Legacy: Replit Object Storage path (nullable for backward compatibility)
  driveFileId: varchar("drive_file_id"), // Google Drive file ID
  driveFolderId: varchar("drive_folder_id"), // Google Drive folder ID where file is stored
  driveWebViewLink: text("drive_web_view_link"), // Google Drive shareable link
  
  uploadedBy: varchar("uploaded_by").references(() => users.id),
  description: text("description"),
  folder: varchar("folder").default("other"), // Document folder for organization
  
  // Enhanced document metadata and OCR capabilities
  isProcessed: boolean("is_processed").default(false),
  processingStatus: varchar("processing_status").default("pending"), // pending, processing, completed, failed
  ocrText: text("ocr_text"), // Extracted text content
  ocrConfidence: decimal("ocr_confidence", { precision: 5, scale: 2 }), // OCR confidence score 0-100
  extractedMetadata: jsonb("extracted_metadata"), // Key-value pairs extracted from document
  documentHash: varchar("document_hash"), // SHA256 hash for duplicate detection
  pageCount: integer("page_count"), // Number of pages for multi-page documents
  
  // Smart classification and content analysis
  autoClassifiedType: documentTypeEnum("auto_classified_type"), // AI-suggested document type
  classificationConfidence: decimal("classification_confidence", { precision: 5, scale: 2 }),
  containsPII: boolean("contains_pii").default(false), // Personal identifiable information flag
  containsFinancialData: boolean("contains_financial_data").default(false),
  languageDetected: varchar("language_detected").default("en"),
  
  // Document relationships and workflow
  parentDocumentId: uuid("parent_document_id"), // For versions/revisions
  isConfidential: boolean("is_confidential").default(false),
  retentionDate: timestamp("retention_date"), // When document can be deleted
  tags: jsonb("tags"), // Flexible tagging system
  
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_documents_claim_id").on(table.claimId),
  index("idx_documents_lead_id").on(table.leadId),
  index("idx_documents_type").on(table.documentType),
  index("idx_documents_processing_status").on(table.processingStatus),
  index("idx_documents_hash").on(table.documentHash),
  index("idx_documents_folder").on(table.folder),
  index("idx_documents_drive_file_id").on(table.driveFileId),
]);


// Document Templates for state-approved documents with token placeholders
export const documentTemplates = pgTable("document_templates", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Template identification
  name: varchar("name").notNull(), // e.g., "NC Welcome Letter", "PA Agreement"
  type: documentTemplateTypeEnum("type").notNull(),
  state: varchar("state", { length: 2 }), // State code (e.g., "NC", "FL", null for universal)
  version: varchar("version").default("1.0"),
  
  // Template content and structure
  title: varchar("title").notNull(), // Display title
  description: text("description"),
  content: text("content").notNull(), // HTML/text content with tokens like {{claim.number}} or ${claim.number}
  tokens: jsonb("tokens"), // Available tokens for this template with descriptions
  
  // Original file storage for .docx templates
  originalFileName: varchar("original_file_name"), // Original uploaded file name
  originalFilePath: text("original_file_path"), // Path to original .docx file in object storage
  originalFileSize: integer("original_file_size"), // File size in bytes
  tokenSyntax: varchar("token_syntax").default("curly"), // 'curly' for {{}} or 'dollar' for ${}
  
  // Template metadata
  isStateRequired: boolean("is_state_required").default(false), // Required by state law
  requiresSignature: boolean("requires_signature").default(false), // Needs DocuSign signature
  displayOrder: integer("display_order").default(0), // Order for UI display
  category: varchar("category").default("other"), // pa_agreement, notice_carrier, appraisal, correspondence, other
  
  // Status and lifecycle
  status: documentTemplateStatusEnum("status").default("active"),
  approvedBy: varchar("approved_by").references(() => users.id), // Who approved this template
  approvedAt: timestamp("approved_at"),
  
  createdBy: varchar("created_by").references(() => users.id).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_document_templates_type").on(table.type),
  index("idx_document_templates_state").on(table.state),
  index("idx_document_templates_status").on(table.status),
  index("idx_document_templates_display_order").on(table.displayOrder),
  index("idx_document_templates_category").on(table.category),
]);


// Document Generations - tracking when templates are processed with claim data
export const documentGenerations = pgTable("document_generations", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // References
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  templateId: uuid("template_id").references(() => documentTemplates.id, { onDelete: "set null" }),
  documentId: uuid("document_id").references(() => documents.id), // Generated PDF document
  
  // Generation details
  templateVersion: varchar("template_version"), // Version of template used
  generatedContent: text("generated_content"), // Processed content with tokens replaced
  tokenValues: jsonb("token_values"), // Actual values used for token replacement
  
  // Processing status
  status: documentGenerationStatusEnum("status").default("pending"),
  statusMessage: text("status_message"), // Error messages or processing notes
  
  // DocuSign integration
  sentToDocusign: boolean("sent_to_docusign").default(false),
  docusignEnvelopeId: varchar("docusign_envelope_id"),
  
  generatedBy: varchar("generated_by").references(() => users.id).notNull(),
  generatedAt: timestamp("generated_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_document_generations_claim_id").on(table.claimId),
  index("idx_document_generations_template_id").on(table.templateId),
  index("idx_document_generations_status").on(table.status),
  index("idx_document_generations_docusign_envelope").on(table.docusignEnvelopeId),
]);


// Document Template Packages - Collections of templates for state + file type
export const documentTemplatePackages = pgTable("document_template_packages", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Package identification
  name: varchar("name").notNull(), // e.g., "NC Public Adjuster Initial Package"
  description: text("description"),
  state: varchar("state", { length: 2 }), // State code (e.g., "NC", "FL", null for universal)
  fileType: varchar("file_type").notNull(), // public_adjuster, appraisal
  scope: varchar("scope").notNull().default("state_specific"), // 'global' or 'state_specific' - global bundles are available across all claims
  
  // Package configuration
  isDefault: boolean("is_default").default(false), // Default package for this state+filetype
  autoGenerateOnStatus: varchar("auto_generate_on_status"), // Claim status that triggers auto-generation
  sendOrder: integer("send_order").default(1), // Order for multi-package workflows
  
  // DocuSign configuration
  autoSendToDocusign: boolean("auto_send_to_docusign").default(false),
  signingOrder: jsonb("signing_order"), // Array of {role, order} objects
  
  // Status
  isActive: boolean("is_active").default(true),
  
  createdBy: varchar("created_by").references(() => users.id).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_document_packages_state").on(table.state),
  index("idx_document_packages_file_type").on(table.fileType),
  index("idx_document_packages_is_active").on(table.isActive),
]);


// Package Templates - Join table linking packages to templates
export const packageTemplates = pgTable("package_templates", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  packageId: uuid("package_id").references(() => documentTemplatePackages.id, { onDelete: "cascade" }).notNull(),
  templateId: uuid("template_id").references(() => documentTemplates.id, { onDelete: "cascade" }).notNull(),
  
  // Template configuration in package
  displayOrder: integer("display_order").default(0), // Order in package
  isRequired: boolean("is_required").default(true), // Required for package generation
  documentOrder: integer("document_order").default(1), // Order in DocuSign envelope
  
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_package_templates_package_id").on(table.packageId),
  index("idx_package_templates_template_id").on(table.templateId),
]);


// Completion Documents table for tracking required documentation
export const completionDocuments = pgTable("completion_documents", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  reminderConfigId: uuid("reminder_config_id").references(() => reminderConfigurations.id),
  
  // Document types
  documentType: varchar("document_type").notNull(), // 'photos', 'invoice', 'certificate_of_completion'
  documentName: varchar("document_name").notNull(),
  documentUrl: text("document_url"),
  documentId: uuid("document_id").references(() => documents.id),
  
  // Status
  isReceived: boolean("is_received").default(false),
  receivedAt: timestamp("received_at"),
  isApproved: boolean("is_approved").default(false),
  approvedBy: varchar("approved_by").references(() => users.id),
  approvedAt: timestamp("approved_at"),
  
  // Notes
  notes: text("notes"),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_completion_docs_claim_id").on(table.claimId),
  index("idx_completion_docs_type").on(table.documentType),
  index("idx_completion_docs_is_received").on(table.isReceived),
]);


// Package Version - Versioning for package templates
export const packageVersions = pgTable("package_versions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Package reference
  packageId: uuid("package_id").references(() => documentTemplatePackages.id, { onDelete: "cascade" }).notNull(),
  
  // Version identification
  version: varchar("version").notNull(), // e.g., "1.0", "1.1", "2.0"
  versionNotes: text("version_notes"), // What changed in this version
  
  // Template snapshot - copy of templates at this version
  templateSnapshot: jsonb("template_snapshot"), // Array of {templateId, templateVersion, displayOrder}
  
  // Publication status
  isDraft: boolean("is_draft").default(true),
  publishedAt: timestamp("published_at"),
  publishedBy: varchar("published_by").references(() => users.id),
  
  // Effective dates
  effectiveFrom: timestamp("effective_from"),
  effectiveTo: timestamp("effective_to"), // Null = current version
  
  createdBy: varchar("created_by").references(() => users.id).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_package_versions_package_id").on(table.packageId),
  index("idx_package_versions_version").on(table.version),
  index("idx_package_versions_effective").on(table.effectiveFrom),
  index("idx_package_versions_is_draft").on(table.isDraft),
]);


// Package Validation Rules - Define required fields per package/document type
export const packageValidationRules = pgTable("package_validation_rules", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // What this rule applies to
  packageId: uuid("package_id").references(() => documentTemplatePackages.id, { onDelete: "cascade" }),
  templateId: uuid("template_id").references(() => documentTemplates.id, { onDelete: "cascade" }),
  
  // Rule name and description
  ruleName: varchar("rule_name").notNull(),
  description: text("description"),
  
  // Field validation
  fieldPath: varchar("field_path").notNull(), // e.g., "claim.lossAddress", "contact.email"
  fieldLabel: varchar("field_label").notNull(), // Human-readable label for error messages
  
  // Validation type
  validationType: varchar("validation_type").notNull(), // required, format, min_length, regex, custom
  validationValue: text("validation_value"), // Value for validation (regex pattern, min value, etc.)
  
  // Error handling
  errorMessage: text("error_message").notNull(), // Message shown when validation fails
  severity: varchar("severity").default("error"), // error, warning
  
  // Quick fix link
  quickFixPath: varchar("quick_fix_path"), // URL path to fix the issue (e.g., "/claims/:id/edit")
  quickFixLabel: varchar("quick_fix_label"), // Button label (e.g., "Edit Claim")
  
  // Status
  isActive: boolean("is_active").default(true),
  displayOrder: integer("display_order").default(0),
  
  createdBy: varchar("created_by").references(() => users.id).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_package_validation_rules_package_id").on(table.packageId),
  index("idx_package_validation_rules_template_id").on(table.templateId),
  index("idx_package_validation_rules_is_active").on(table.isActive),
]);


// Document Content Learning - Extract and learn from document content
export const documentContentLearning = pgTable("document_content_learning", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Document reference
  documentId: uuid("document_id").references(() => documents.id, { onDelete: "cascade" }).notNull(),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "set null" }),
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "set null" }),

  // Document classification
  documentType: varchar("document_type").notNull(),
  documentSubtype: varchar("document_subtype"),
  carrier: varchar("carrier"),
  
  // Content extraction
  extractedText: text("extracted_text"),
  structuredData: jsonb("structured_data"),
  
  // Key elements for specific document types
  keyDates: jsonb("key_dates"),
  deadlines: jsonb("deadlines"),
  monetaryAmounts: jsonb("monetary_amounts"),
  namedEntities: jsonb("named_entities"),
  legalClauses: jsonb("legal_clauses"),
  
  // Document-specific learning (e.g., for demands, ROR letters)
  effectiveLanguage: jsonb("effective_language"),
  commonClauses: jsonb("common_clauses"),
  toneAnalysis: jsonb("tone_analysis"),
  
  // Outcome tracking (did this document lead to good results?)
  wasEffective: boolean("was_effective"),
  effectivenessNotes: text("effectiveness_notes"),
  associatedSettlementIncrease: decimal("associated_settlement_increase", { precision: 12, scale: 2 }),
  
  // Training value
  qualityScore: integer("quality_score"),
  isUsefulForTraining: boolean("is_useful_for_training").default(true),
  trainingExported: boolean("training_exported").default(false),
  
  analyzedBy: varchar("analyzed_by"),
  analyzedAt: timestamp("analyzed_at").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_document_content_learning_doc").on(table.documentId),
  index("idx_document_content_learning_claim").on(table.claimId),
  index("idx_document_content_learning_type").on(table.documentType),
  index("idx_document_content_learning_carrier").on(table.carrier),
  index("idx_document_content_learning_training").on(table.isUsefulForTraining, table.trainingExported),
  index("idx_document_content_learning_org").on(table.orgId),
]);


// ============================================================================
// RAG System Tables
// ============================================================================

// Document Chunks — Chunked text with vector embeddings for RAG retrieval
export const documentChunks = pgTable("document_chunks", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Source references
  documentId: uuid("document_id").references(() => documents.id, { onDelete: "cascade" }),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }),
  sourceType: varchar("source_type").notNull(), // document, knowledge_base, admin_instruction
  sourceId: uuid("source_id"), // ID of the source entity (knowledge_base_entry id, etc.)
  
  // Chunk data
  chunkIndex: integer("chunk_index").notNull(), // Order within source document
  content: text("content").notNull(), // The chunk text (~500 tokens)
  embedding: text("embedding"), // Serialized vector (1536 dimensions), stored as text until pgvector migration
  embeddingModel: varchar("embedding_model").default("text-embedding-3-small"),
  
  // Metadata
  metadata: jsonb("metadata"), // Document name, type, page number, section, etc.
  tokenCount: integer("token_count"), // Actual token count of this chunk
  contentHash: varchar("content_hash"), // Hash of content for dedup/change detection
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_document_chunks_document").on(table.documentId),
  index("idx_document_chunks_claim").on(table.claimId),
  index("idx_document_chunks_source").on(table.sourceType, table.sourceId),
  index("idx_document_chunks_content_hash").on(table.contentHash),
]);
